import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import { DeliveryType } from '../types';
import { ICart, IGuestCart } from '../interfaces';
import { IUser } from '../../../../interfaces/user';
import { ILocation } from '../../../../interfaces/locations';
import { useFormatMessage } from '../../../../hooks';
import { actions as cartActions } from '../../../../store/ducks/cart.duck';
import { selectCart, selectGuestCart, selectIsAuthorized, selectMe } from '../selectors';
import { IStore } from '../../../../interfaces/store';

const getInitialValues = (
  cart: ICart | null,
  isAuthorized: boolean,
  me: IUser | undefined,
  localLocation: ILocation,
  guestCart: IGuestCart | null
) => {
  let phone = '';
  let receivedAt = '';

  if (isAuthorized && cart) {
    phone =
      cart.phone && cart.phone.replace(`+ `, '')[0] === '1'
        ? `1${cart.phone}`
        : cart.phone || '';
  } else {
    phone = guestCart?.phone || '';
  }

  if (isAuthorized && cart && cart.received_at) {
    receivedAt = cart.received_at.replace(/\+.*/, '');
  } else if (guestCart) {
    receivedAt = guestCart
      ? guestCart.received_at
      : moment(Date.now()).format('YYYY-MM-DD[T]HH:mm');
  }

  return {
    deliveryType: DeliveryType.delivery,
    location: {
      id: undefined,
      address: isAuthorized
        ? me?.location?.address || localLocation?.address || ''
        : localLocation?.address || '',
      lng: isAuthorized ? me?.location?.lng || 0 : localLocation?.lng || 0,
      lat: isAuthorized ? me?.location?.lat || 0 : localLocation?.lat || 0,
      city: '',
      country: '',
      house: '',
      province: '',
      street: '',
      working_times: undefined,
    },
    phone,
    fio: isAuthorized && cart ? cart.fio : guestCart?.fio || '',
    email: isAuthorized && cart ? cart.email : guestCart?.email || '',
    received_at: receivedAt,
    useBonuse: 0,
  };
};

export const useFormikCart = ({
  localLocation,
  pickupLocation,
  setAuthDialogOpen,
}: {
  localLocation: ILocation;
  pickupLocation: IStore | null;
  setAuthDialogOpen: (open: boolean) => void;
}) => {
  const fm = useFormatMessage();
  const dispatch = useDispatch();
  const cart = useSelector(selectCart);
  const me = useSelector(selectMe);
  const isAuthorized = useSelector(selectIsAuthorized);
  const guestCart = useSelector(selectGuestCart);
  return useFormik({
    initialValues: getInitialValues(cart, isAuthorized, me, localLocation, guestCart),

    validationSchema: () =>
      Yup.lazy(values => {
        return values.deliveryType === DeliveryType.delivery
          ? Yup.object().shape({
              location: Yup.object({
                address: Yup.string()
                  .required(fm('AUTH.VALIDATION.REQUIRED_FIELD'))
                  .nullable(),
              }),
            })
          : Yup.object().shape({});
      }),

    onSubmit: async values => {
      if (!isAuthorized) {
        const { location } = values;

        values.deliveryType === DeliveryType.delivery && location
          ? localStorage.setItem(
              'location',
              JSON.stringify(location.address.trim().length > 0 ? location : null)
            )
          : localStorage.removeItem('location');

        dispatch(
          cartActions.setInfoUserGuestCart({
            fio: values.deliveryType === DeliveryType.delivery ? values.fio : '',
            email: values.deliveryType === DeliveryType.delivery ? values.email : '',
            phone: values.deliveryType === DeliveryType.delivery ? values.phone : '',
            received_at: moment(values.received_at).format('YYYY-MM-DD[T]HH:mm:ss') || '',
            isPickup: values.deliveryType === DeliveryType.self,
            pickupStore: values.deliveryType === DeliveryType.self ? pickupLocation : null,
            address:
              values.deliveryType === DeliveryType.delivery
                ? values.location.address
                : values.location.address,
          })
        );
        setAuthDialogOpen(true);
      } else {
        dispatch(
          cartActions.checkoutRequest({
            data: {
              ...values,
              fio: values.deliveryType === DeliveryType.delivery ? values.fio : '',
              email: values.deliveryType === DeliveryType.delivery ? values.email : '',
              received_at: moment(values.received_at).format('YYYY-MM-DD[T]HH:mm:ss'),
              address: values.location?.address || '',
              deliveryType: values.deliveryType,
            },
          })
        );
      }
    },
  });
};
